import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from "react";
import { User, Session } from '@supabase/supabase-js';
import { supabase, cleanupAuthState } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface AuthContextType {
  isAuthenticated: boolean | null;
  user: User | null;
  session: Session | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signup: (email: string, password: string, metadata?: { full_name?: string, whatsapp_number?: string, referral_code?: string }) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Estado para armazenar as informações de autenticação
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Função para fazer login com e-mail e senha
  const login = useCallback(async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      setLoading(true);
      console.log('🔐 Iniciando login via AuthContext...');

      // Limpar estado existente
      cleanupAuthState();

      // Tentar logout global primeiro
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continuar mesmo se isso falhar
        console.error("Erro ao fazer logout global:", err);
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      console.log("Login bem-sucedido:", data);

      // Salvar localmente se "lembrar de mim" estiver ativado
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }

      // O estado será atualizado pelo evento onAuthStateChange
    } catch (error: any) {
      console.error("Erro no login:", error.message);
      toast({
        title: "Erro no login",
        description: error.message || "Não foi possível fazer login. Tente novamente.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Função para cadastrar um novo usuário
  const signup = useCallback(async (email: string, password: string, metadata?: {
    full_name?: string,
    whatsapp_number?: string,
    referral_code?: string
  }) => {
    try {
      setLoading(true);
      console.log('📝 Iniciando cadastro via AuthContext...');

      // Limpar estado existente
      cleanupAuthState();

      const result = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: metadata?.full_name,
            whatsapp_number: metadata?.whatsapp_number,
            referral_code: metadata?.referral_code
          }
        }
      });

      if (result.error) {
        throw result.error;
      }

      console.log("Cadastro bem-sucedido:", result.data);

      toast({
        title: "Cadastro realizado",
        description: "Sua conta foi criada com sucesso.",
      });

      // O estado será atualizado pelo evento onAuthStateChange
    } catch (error: any) {
      console.error("Erro no cadastro:", error.message);
      toast({
        title: "Erro no cadastro",
        description: error.message || "Não foi possível criar sua conta. Tente novamente.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Função para fazer logout
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🚪 Iniciando logout via AuthContext...');

      // Limpar estado de autenticação
      cleanupAuthState();

      // Remover preferência de "lembrar de mim"
      localStorage.removeItem('rememberMe');

      // Tentar logout global
      await supabase.auth.signOut({ scope: 'global' });

      // Garantir estado limpo
      setIsAuthenticated(false);
      setUser(null);
      setSession(null);

      console.log("Logout bem-sucedido");

      toast({
        title: "Logout realizado",
        description: "Você saiu da sua conta com sucesso.",
      });
    } catch (error: any) {
      console.error("Erro no logout:", error.message);
      toast({
        title: "Erro no logout",
        description: "Ocorreu um erro ao sair. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Configurar o listener de alterações de estado de autenticação
  useEffect(() => {
    // Função para identificar se há uma sessão ativa
    const initAuth = async () => {
      try {
        setLoading(true);
        console.log("Inicializando provider de autenticação otimizado...");

        // Definir o listener ANTES de verificar a sessão
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (event, currentSession) => {
            console.log("Mudança de estado de autenticação:", event, !!currentSession);

            // Atualizar estado sincronamente
            setSession(currentSession);
            setUser(currentSession?.user ?? null);
            setIsAuthenticated(!!currentSession);
            setLoading(false);

            // Para eventos SIGNED_IN e TOKEN_REFRESHED, atualizar localStorage para compatibilidade
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
              localStorage.setItem('isAuthenticated', 'true');
              if (currentSession?.user?.email) {
                localStorage.setItem('userEmail', currentSession.user.email);
              }
              if (currentSession?.user?.user_metadata?.full_name) {
                localStorage.setItem('userName', currentSession.user.user_metadata.full_name);
              }
            }

            // Para o evento SIGNED_OUT, limpar localStorage
            if (event === 'SIGNED_OUT') {
              cleanupAuthState();
            }
          }
        );

        // Verificar se já existe uma sessão
        const { data: { session: currentSession } } = await supabase.auth.getSession();

        // Definir estado inicial baseado na sessão existente
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsAuthenticated(!!currentSession);

        // Se existir sessão, atualizar localStorage para compatibilidade
        if (currentSession?.user) {
          localStorage.setItem('isAuthenticated', 'true');
          if (currentSession.user.email) {
            localStorage.setItem('userEmail', currentSession.user.email);
          }
          if (currentSession.user.user_metadata?.full_name) {
            localStorage.setItem('userName', currentSession.user.user_metadata.full_name);
          }
        }

        console.log("Autenticação inicializada com otimizações, sessão:", !!currentSession);

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Erro ao inicializar autenticação:", error);
        setIsAuthenticated(false);
        setUser(null);
        setSession(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Memoize context value for performance
  const contextValue = useMemo(() => ({
    isAuthenticated,
    user,
    session,
    login,
    signup,
    logout,
    loading
  }), [isAuthenticated, user, session, login, signup, logout, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
